<template>
  <ERow id="upload-panel" no-gutters justify="center">
    <ECol class="ma-2">
      <FileBox
        :allowedTypes="allowedTypes"
        :disabled="
          ingestFileUploaderStore.uploadStats &&
          ingestFileUploaderStore.uploadStats.isUploading
        "
      />

      <v-card-actions class="my-2">
        <v-spacer />
        <v-btn
          color="white grey--text"
          elevation="0"
          :disabled="
            ingestFileUploaderStore.uploadStats &&
            ingestFileUploaderStore.uploadStats.isUploading
          "
          @click="clearForm()"
        >
          Clear
        </v-btn>
        <v-btn
          color="primary"
          :disabled="
            areFilesEmpty ||
            (ingestFileUploaderStore.uploadStats &&
              ingestFileUploaderStore.uploadStats.isUploading)
          "
          @click="processUpload"
        >
          Upload
        </v-btn>
      </v-card-actions>
    </ECol>
  </ERow>
</template>

<script>
import { ALLOWED_DRONE_TYPES } from "@evercam/shared/constants/ingest"
import FileBox from "@/components/ingest/FileBox"
import exifr from "exifr"
import * as tus from "tus-js-client/dist/tus.min.js"
import { AdminApi } from "@evercam/shared/api/adminApi"
import { IngestApi } from "@evercam/shared/api/ingestApi"
import { useIngestFileUploaderStore } from "@/stores/ingestFileUploader"
import { mapStores } from "pinia"
import { useAccountStore } from "@/stores/account"

export default {
  components: {
    FileBox,
  },
  props: {
    token: {
      type: String,
      default: "",
    },
    projectId: {
      type: String,
      default: "",
    },
  },
  data: () => ({
    uploadedUrls: [],
    allowedTypes: ALLOWED_DRONE_TYPES,
    flightMetadata: undefined,
  }),
  computed: {
    ...mapStores(useAccountStore, useIngestFileUploaderStore),
    areFilesEmpty() {
      return this.ingestFileUploaderStore.files.length === 0
    },
  },
  mounted() {
    this.ingestFileUploaderStore.selectedFileType = "images"
    this.ingestFileUploaderStore.files = []
  },
  methods: {
    async processUpload() {
      if (this.areFilesEmpty) {
        this.$notifications.error("Please provide files to upload")

        return
      }

      try {
        this.ingestFileUploaderStore.uploadStats.isUploading = true
        let fileIndex = 0
        while (
          !this.flightMetadata &&
          fileIndex < this.ingestFileUploaderStore.files.length
        ) {
          this.flightMetadata = await this.extractMetadataFromFile(
            this.ingestFileUploaderStore.files[fileIndex]
          )
          fileIndex++
        }
        if (this.flightMetadata) {
          try {
            await IngestApi.drone.prepareForUpload(this.projectId, {
              token: this.token,
              uploadedBy: this.accountStore.email,
            })
          } catch (e) {
            this.$notifications.error({
              text: "Preparing for Upload failed: " + e,
              error: e,
            })

            return
          }
          this.ingestFileUploaderStore.uploadStats.percentage = 0
          for (let i = 0; i < this.ingestFileUploaderStore.files.length; i++) {
            this.uploadedUrls.push(
              await this.uploadFile(this.ingestFileUploaderStore.files[i])
            )
          }
          let selectedProject = await AdminApi.projects.show(this.projectId)
          selectedProject = await selectedProject.data
          this.uploadToIngest(selectedProject.name ?? "")
        } else {
          this.$notifications.error({
            text: "No metadata found, check files and try again",
            error: {},
          })
        }
      } catch (error) {
        this.$notifications.error({
          text: `Couldn't upload files : ${error}`,
          error: error,
        })
        this.ingestFileUploaderStore.uploadStats = {
          percentage: 0,
          totalSize: 0,
          uploadedSize: 0,
          isUploading: false,
          isProcessing: false,
        }
      }
    },
    async uploadFile(file) {
      return new Promise((resolve, reject) => {
        const upload = new tus.Upload(file, {
          endpoint: `${this.$config.public.portalURL}`,
          chunkSize: Infinity,
          retryDelays: [0, 1000, 3000, 5000],
          parallelUploads: 1,
          metadata: {
            filename: file.name,
            filetype: file.type,
          },
          onProgress: (bytesUploaded) => {
            this.ingestFileUploaderStore.uploadStats.percentage = (
              ((this.ingestFileUploaderStore.uploadStats.uploadedSize +
                bytesUploaded) /
                this.ingestFileUploaderStore.uploadStats.totalSize) *
              100
            ).toFixed(2)
          },
          onSuccess: () => {
            this.ingestFileUploaderStore.uploadStats.uploadedSize += file.size
            this.ingestFileUploaderStore.uploadStats.percentage = (
              (this.ingestFileUploaderStore.uploadStats.uploadedSize /
                this.ingestFileUploaderStore.uploadStats.totalSize) *
              100
            ).toFixed(2)

            file.uploadPercentage = "100"
            const [title, fileExtension] = upload.file.name.split(".")
            resolve({
              url: upload.url,
              title,
              fileExtension,
            })
          },
          onError: (error) => {
            console.log("Failed because: " + error)
            reject(error)
          },
        })
        upload.findPreviousUploads().then((previousUploads) => {
          // Found previous uploads so we select the first one.
          if (previousUploads.length) {
            upload.resumeFromPreviousUpload(previousUploads[0])
          }

          // Start the upload
          upload.start()
        })
      })
    },
    uploadToIngest(projectName) {
      this.ingestFileUploaderStore.uploadStats.isProcessing = true
      this.ingestFileUploaderStore.uploadStats.isUploading = false
      IngestApi.drone
        .upload(
          this.projectId,
          {
            token: this.token,
            uploadedBy: this.accountStore.email,
          },
          {
            flightMetadata: this.flightMetadata,
            upload: this.uploadedUrls,
            projectName: projectName,
          }
        )
        .then(() => {
          this.ingestFileUploaderStore.uploadStats = {
            percentage: 0,
            totalSize: 0,
            uploadedSize: 0,
            isUploading: false,
            isProcessing: false,
          }
          this.uploadedUrls = []
          this.$emit("upload-completed")
        })
        .catch((error) => {
          this.$notifications.error({ text: "Error uploading images", error })
          this.uploadedUrls = []
          this.ingestFileUploaderStore.uploadStats = {
            percentage: 0,
            totalSize: 0,
            uploadedSize: 0,
            isUploading: false,
            isProcessing: false,
          }
        })

      return
    },
    clearForm() {
      this.ingestFileUploaderStore.files = []
      this.floor = ""
      this.date = ""
    },
    formatGPSCoordinate(coord, coordRef) {
      if (coord.length > 2)
        return coord[0] + "°" + coord[1] + "'" + coord[2] + `" ${coordRef}`

      return ""
    },
    formatEXIFDateTime(dateTime) {
      return this.$moment(dateTime).format("YYYY-MM-DD hh:mm:ss")
    },
    async extractMetadataFromFile(file) {
      const data = await exifr.parse(file)
      if (data) {
        data.DateTime = this.formatEXIFDateTime(data.DateTimeOriginal)

        const latitudeFormatted = this.formatGPSCoordinate(
          data.GPSLatitude,
          data.GPSLatitudeRef
        )
        const longitudeFormatted = this.formatGPSCoordinate(
          data.GPSLongitude,
          data.GPSLongitudeRef
        )
        if (latitudeFormatted && longitudeFormatted) {
          return {
            dateTime: data.DateTime,
            gpsAltitude: data.GPSAltitude.toString(),
            gpsCoordinates: latitudeFormatted + "," + longitudeFormatted,
          }
        }
      }
    },
  },
}
</script>

<style lang="scss">
#upload-panel {
  z-index: 2 !important;
}
</style>
